import { NoticeDetailResponse } from "@apis/notice/types";
import { ICONS } from "@assets/icons";
import Button from "@components/atoms/Button";
import ContentBox from "@components/atoms/ContentBox";
import IconBox from "@components/atoms/IconBox";
import RenderEventCategoryContent from "@components/molecules/RenderEventCategoryContent";
import { ApiUrls } from "@constants/api-urls";
import { PrivateRoutes } from "@constants/urls";
import useCommonDelete from "@hooks/useCommonDelete";
import useCommonEdit from "@hooks/useCommonEdit";
import useFetchDetailData from "@hooks/useFetchDetailData";
import useGlobalModal from "@hooks/useGlobalModal";
import useRouter from "@hooks/useRouter";
import { colors, typo } from "@styles/index";
import { RenderCommonElementByType } from "@utils/DynamicComponentUtils";
import isCheckValueExistence from "@utils/check-value-existence";
import { compareObjects } from "@utils/data-fns";
import isPreviewValiDate from "@utils/preview-validate";
import { useFormik } from "formik";
import React, { useCallback } from "react";
import styled, { css } from "styled-components";
import { NoticeDetailData, NoticeDetailInitValuesType } from "./Data";
import CopyButton from "@components/atoms/CopyButton";

const NoticeDetail: React.FC = () => {
  const HEADER_TITLE = "공지사항 등록";
  const CONTENT_TITLE = "공지사항 내용";
  const PREVIEW_URL = "notice";

  // Data
  const { createBodyKey, formInitialValues, contentsOption } = NoticeDetailData;

  // Formik
  const formik = useFormik({
    initialValues: formInitialValues,
    onSubmit: (values) => {
      editMutation(values);
    },
  });

  // Hooks
  const { query, goBack, pathname } = useRouter();
  const {
    hideModal,
    showConfirmModal,
    showReasonModificationModal,
    showReasonEditModal,
  } = useGlobalModal();
  const id = query && query.id ? query.id : "";
  const { initData } = useFetchDetailData<NoticeDetailResponse>({
    id: id,
    queryKey: "noticeDetail",
    fetchUrl: ApiUrls.noticeDetail,
    formik,
  });
  const { editMutation } = useCommonEdit<NoticeDetailInitValuesType>({
    editUrl: ApiUrls.noticeDetail,
    createBodyKey,
  });
  const { handleDelete } = useCommonDelete({ deleteUrl: ApiUrls.noticeDelete });

  // Functions
  const handleGoBackClick = () => {
    if (JSON.stringify(initData) !== JSON.stringify(formik?.values)) {
      showConfirmModal({
        title: `작성한 내용을 저장하지 않고\n 취소 하시겠어요?`,
        firstButtonText: "닫기",
        secondButtonText: "확인",
        onConfirm: () => {
          hideModal();
          goBack();
        },
      });
    } else {
      goBack();
    }
  };

  const handlePreview = () => {
    const previewData = formik.values;
    localStorage.setItem("previewData", JSON.stringify(previewData));
    window.open(`${PrivateRoutes.PreView}?type=${PREVIEW_URL}`, "_blank");
  };

  const handleEditContent = () => {
    showReasonEditModal({
      category: "notice",
      onSave: () => {
        localStorage.removeItem("previewData");
        hideModal();
        formik?.handleSubmit();
      },
      onChangeReason: (e) => {
        formik.setFieldValue("updateReason", e.target.value);
      },
    });
  };

  const handleDeleteContent = () => {
    showConfirmModal({
      title: `삭제하시겠어요?`,
      firstButtonText: "닫기",
      secondButtonText: "삭제하기",
      onConfirm: () => {
        handleDelete(id);
        hideModal();
      },
    });
  };

  const handleReasonModification = () => {
    showReasonModificationModal({
      category: "notice",
      updateHistory: formik?.values.updateHistory,
    });
  };

  const isEditButtonValidate = () => {
    if (!initData) return true;
    const { values: formikValues } = formik;

    return (
      !isCheckValueExistence({
        formikValues,
        pathname,
        validateKey: createBodyKey,
      }) || compareObjects(initData, formikValues)
    );
  };

  const isPreviewValidateCheck = useCallback(() => {
    return !isPreviewValiDate({
      formikValues: formik.values,
      pathname,
    });
  }, [formik.values]);

  return (
    <ContentsWrap>
      <HeaderWrap>
        <IconBox
          width={32}
          height={32}
          src={ICONS.ARROW_ICON.BACK}
          isCursorPointer
          onClick={handleGoBackClick}
        />
        <HeaderTitle>{HEADER_TITLE}</HeaderTitle>
      </HeaderWrap>

      <div>
        <ContentBox style={{ padding: 0 }}>
          <ContentsHeader>
            {CONTENT_TITLE}
            <ButtonWrap>
              <CopyButton text="외부 링크 복사" type="external" />
              <CopyButton text="앱 연결 링크 복사" type="app" />
            </ButtonWrap>
          </ContentsHeader>

          <ContentsMainWrap>
            {contentsOption?.map(({ label, ...res }, _) => {
              return (
                <React.Fragment key={`${_}_contents`}>
                  <InputWrap>
                    <LabelWrap>
                      <LabelInnerWrap>
                        <Label>{label || "LABEL"}</Label>
                      </LabelInnerWrap>
                    </LabelWrap>
                    <InputContentsWrap>
                      {RenderCommonElementByType({
                        formik,
                        ...res,
                      })}
                    </InputContentsWrap>
                  </InputWrap>
                </React.Fragment>
              );
            })}

            <RenderEventCategoryContent
              formik={formik}
              category={formik.values.contentCategory}
            />
          </ContentsMainWrap>
        </ContentBox>

        <FooterButtonWrap>
          <LeftButtonWrap>
            <Button
              text="삭제"
              buttonType="line_red"
              onClick={handleDeleteContent}
            />
            <Button
              text="미리보기"
              buttonType="line_primary"
              disabled={isPreviewValidateCheck()}
              onClick={handlePreview}
            />
            <Button
              text="수정사유 확인"
              buttonType="line_primary"
              onClick={handleReasonModification}
              disabled={formik?.values?.updateHistory?.length === 0}
            />
          </LeftButtonWrap>
          <RightButtonWrap>
            <Button
              text="취소"
              buttonType="line_black"
              onClick={handleGoBackClick}
            />
            <Button
              text="저장하기"
              disabled={isEditButtonValidate()}
              onClick={handleEditContent}
            />
          </RightButtonWrap>
        </FooterButtonWrap>
      </div>
    </ContentsWrap>
  );
};

export default NoticeDetail;

const ContentsWrap = styled.div`
  background-color: ${colors.GRAY11};
  height: 100%;
  padding: 40px;
  overflow: auto;
`;

const HeaderWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`;

const MainHeaderTitle = styled.h1`
  ${typo.DISPLAY_3}
  color: ${colors.GRAY2};
  margin-bottom: 24px;
`;

const HeaderTitle = styled(MainHeaderTitle)`
  margin: 0;
`;

const TableTitle = styled.div`
  color: ${colors.GRAY2};
  ${typo.HEADING_4};
  margin-bottom: 18px;
`;

const ContentTitle = styled(TableTitle)`
  margin: 0;
  padding: 24px;
  border-bottom: 1px solid ${colors.GRAY9};
`;

const ContentsHeader = styled(ContentTitle)`
  display: flex;
  justify-content: space-between;
`;

const ButtonWrap = styled.div`
  display: flex;
  gap: 12px;
`;

const ContentsMainWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  max-width: 840px;
  margin: auto;
  padding: 52px 0 80px;
`;

const InputWrap = styled.div`
  display: flex;
  gap: 12px;
  min-height: 48px;
  width: 100%;
`;

const LabelWrap = styled.span`
  min-width: 200px;
  padding: 12px 0 12px;
`;

const LabelInnerWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;
const Label = styled.p`
  color: ${colors.GRAY6};
  ${typo.HEADING_7};
`;

const InputContentsWrap = styled.div<{ noti?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;

  ${({ noti }) =>
    noti &&
    css`
      flex-direction: column;
      gap: 12px;
    `}
`;

const FooterButtonWrap = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 24px;
`;

const LeftButtonWrap = styled.div`
  display: flex;
  gap: 12px;
`;

const RightButtonWrap = styled.div`
  display: flex;
  gap: 12px;
`;
